import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, GraduationCap, Building2, Users, TrendingUp, Clock, Award, Euro, Briefcase } from 'lucide-react';

const HERO_IMAGE = "https://images.unsplash.com/photo-1741638511444-4fff9a62053d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBidXNpbmVzcyUyMHNjaG9vbCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzU3NDQwMDN8MA&ixlib=rb-4.1.0&q=85";
const STUDENTS_IMAGE = "https://images.unsplash.com/photo-1543269865-cbf427effbad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwc3R1ZGVudHMlMjB3b3JraW5nJTIwbGFwdG9wfGVufDB8fHx8MTc3NTc0Mzk5Mnww&ixlib=rb-4.1.0&q=85";

const keyFigures = [
  { icon: Clock, value: '490h', label: 'de formation centre' },
  { icon: TrendingUp, value: '>75%', label: "taux d'insertion" },
  { icon: Euro, value: '0€', label: 'frais de scolarité' },
  { icon: Briefcase, value: '80%', label: 'temps en entreprise' },
];

const testimonials = [
  {
    quote: "La formation m'a permis d'acquérir des compétences concrètes. Après 6 mois, j'ai décroché un CDI dans une entreprise du BTP.",
    name: "Karim L.",
    role: "Promotion 2024",
    company: "Commercial BTP"
  },
  {
    quote: "Le rythme 1 jour en centre, 4 jours en entreprise est idéal. On apprend vraiment sur le terrain.",
    name: "Sarah M.",
    role: "Promotion 2024",
    company: "Négociatrice Tech"
  },
  {
    quote: "L'accompagnement de l'équipe pédagogique est exceptionnel. Ils nous préparent vraiment au monde professionnel.",
    name: "Thomas B.",
    role: "Promotion 2024",
    company: "Commercial Services"
  }
];

export const Home = () => {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMAGE} 
            alt="Institut BIA Campus" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0B1F3A]/80"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="max-w-3xl">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-6 block animate-fade-in-up">
              Centre de Formation par Apprentissage
            </span>
            <h1 className="font-serif-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6 animate-fade-in-up animation-delay-100">
              Bâtir • Innover • Accomplir
            </h1>
            <p className="text-lg text-[#F5F5F5]/90 mb-8 leading-relaxed animate-fade-in-up animation-delay-200">
              Devenez expert commercial en vente B2B. Une formation en alternance reconnue par l'État, 
              100% gratuite et rémunérée dès le premier jour.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
              <Link to="/etudiants#candidature">
                <Button className="bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none px-8 py-6 text-base font-semibold" data-testid="hero-cta-student">
                  Candidater maintenant
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/employeurs">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#0B1F3A] rounded-none px-8 py-6 text-base" data-testid="hero-cta-employer">
                  Recruter un apprenti
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Figures Section */}
      <section className="py-20 bg-white" data-testid="key-figures-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              En chiffres
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-[#0B1F3A]">
              Une formation d'excellence
            </h2>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {keyFigures.map((figure, index) => (
              <div 
                key={index}
                className="text-center p-8 border border-[#E6E6E6] hover:border-[#C8A75A] transition-colors hover-lift"
                data-testid={`key-figure-${index}`}
              >
                <figure.icon className="mx-auto mb-4 text-[#C8A75A]" size={32} strokeWidth={1.5} />
                <div className="font-serif-heading text-4xl font-semibold text-[#0B1F3A] mb-2">
                  {figure.value}
                </div>
                <div className="text-sm text-[#4A4A4A] uppercase tracking-wide">
                  {figure.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formation Overview */}
      <section className="py-24 bg-[#F5F5F5]" data-testid="formation-overview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
                Notre Formation
              </span>
              <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-[#0B1F3A] mb-6">
                TP Négociateur Technico-Commercial
              </h2>
              <div className="w-16 h-0.5 bg-[#C8A75A] mb-6"></div>
              <p className="text-[#4A4A4A] mb-8 leading-relaxed">
                Une formation de 12 mois en alternance pour maîtriser l'art de la vente B2B. 
                Diplôme reconnu par l'État, équivalent Bac+2 (RNCP Niveau 5).
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#0B1F3A] flex items-center justify-center">
                    <GraduationCap className="text-[#C8A75A]" size={20} />
                  </div>
                  <span className="text-[#0B1F3A] font-medium">Titre reconnu par l'État - RNCP39063</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#0B1F3A] flex items-center justify-center">
                    <Building2 className="text-[#C8A75A]" size={20} />
                  </div>
                  <span className="text-[#0B1F3A] font-medium">Rythme : 1 jour CFA / 4 jours entreprise</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#0B1F3A] flex items-center justify-center">
                    <Users className="text-[#C8A75A]" size={20} />
                  </div>
                  <span className="text-[#0B1F3A] font-medium">100% présentiel à Saint-Denis (93)</span>
                </div>
              </div>
              
              <Link to="/formation">
                <Button className="bg-[#0B1F3A] text-white hover:bg-[#061326] rounded-none px-6" data-testid="formation-cta">
                  Découvrir le programme
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <img 
                src={STUDENTS_IMAGE}
                alt="Étudiants Institut BIA"
                className="w-full h-auto"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#0B1F3A] p-6 text-white">
                <div className="font-serif-heading text-3xl font-semibold text-[#C8A75A]">3</div>
                <div className="text-sm uppercase tracking-wide">rentrées par an</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dual CTA Section */}
      <section className="py-24 bg-white" data-testid="dual-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Student Card */}
            <div className="bg-[#0B1F3A] p-10 text-white hover-lift" data-testid="student-cta-card">
              <GraduationCap className="text-[#C8A75A] mb-6" size={40} strokeWidth={1.5} />
              <h3 className="font-serif-heading text-2xl font-semibold mb-4">
                Vous êtes étudiant ?
              </h3>
              <p className="text-[#F5F5F5]/80 mb-6 leading-relaxed">
                Rejoignez une formation 100% gratuite, rémunérée dès le 1er jour, 
                avec un emploi à la clé.
              </p>
              <ul className="space-y-2 mb-8 text-sm">
                <li className="flex items-center gap-2">
                  <Award className="text-[#C8A75A]" size={16} />
                  Salaire de 775€ à 1 867€/mois
                </li>
                <li className="flex items-center gap-2">
                  <Award className="text-[#C8A75A]" size={16} />
                  Formation financée par OPCO
                </li>
                <li className="flex items-center gap-2">
                  <Award className="text-[#C8A75A]" size={16} />
                  Passerelle vers Bachelor Bac+3
                </li>
              </ul>
              <Link to="/etudiants">
                <Button className="bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none w-full" data-testid="student-section-cta">
                  Espace Étudiants
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>

            {/* Employer Card */}
            <div className="border-2 border-[#0B1F3A] p-10 hover-lift" data-testid="employer-cta-card">
              <Building2 className="text-[#0B1F3A] mb-6" size={40} strokeWidth={1.5} />
              <h3 className="font-serif-heading text-2xl font-semibold text-[#0B1F3A] mb-4">
                Vous êtes employeur ?
              </h3>
              <p className="text-[#4A4A4A] mb-6 leading-relaxed">
                Recrutez un apprenti formé sur le terrain, opérationnel en 2 mois, 
                avec une aide de l'État et des charges réduites.
              </p>
              <ul className="space-y-2 mb-8 text-sm text-[#0B1F3A]">
                <li className="flex items-center gap-2">
                  <Award className="text-[#C8A75A]" size={16} />
                  Exonération de charges (contrat apprentissage)
                </li>
                <li className="flex items-center gap-2">
                  <Award className="text-[#C8A75A]" size={16} />
                  Aide État jusqu'à 4 500€ (PME)
                </li>
                <li className="flex items-center gap-2">
                  <Award className="text-[#C8A75A]" size={16} />
                  Apprenti présent 80% du temps
                </li>
              </ul>
              <Link to="/employeurs">
                <Button className="bg-[#0B1F3A] text-white hover:bg-[#061326] rounded-none w-full" data-testid="employer-section-cta">
                  Espace Employeurs
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-[#0B1F3A]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Témoignages
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-white">
              Ils nous font confiance
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur p-8 border border-white/10"
                data-testid={`testimonial-${index}`}
              >
                <div className="text-[#C8A75A] text-4xl font-serif-heading mb-4">"</div>
                <p className="text-[#F5F5F5]/90 mb-6 italic leading-relaxed">
                  {testimonial.quote}
                </p>
                <div className="border-t border-white/10 pt-4">
                  <div className="font-semibold text-white">{testimonial.name}</div>
                  <div className="text-sm text-[#C8A75A]">{testimonial.role}</div>
                  <div className="text-sm text-[#F5F5F5]/60">{testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-[#F5F5F5]" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
            Prochaines rentrées
          </span>
          <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-[#0B1F3A] mb-6">
            Septembre 2026 • Janvier 2027 • Avril 2027
          </h2>
          <p className="text-[#4A4A4A] mb-8 max-w-2xl mx-auto">
            Rejoignez l'Institut BIA et construisez votre carrière dans la vente B2B. 
            Places limitées à 20 par promotion.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/etudiants#candidature">
              <Button className="bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none px-8 py-6 text-base font-semibold" data-testid="final-cta-student">
                Déposer ma candidature
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white rounded-none px-8 py-6 text-base" data-testid="final-cta-contact">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
